import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { axisProps, chartColors, formatMonth } from '@shared/lib/chart-theme';
import { ChartTooltip } from '@features/analytics/ui/ChartTooltip';
import type { MonthlyCount } from '@features/analytics/model/dashboard.types';

/**
 * Trend over time for a single series: an area chart in the primary hue. One
 * series means no legend — the card title names it.
 */
export function SignupsChart({ data }: { data: MonthlyCount[] }) {
  const points = data.map((point) => ({
    month: formatMonth(point.month),
    signups: point.count,
  }));

  return (
    <ResponsiveContainer width="100%" height={172}>
      {/* No negative left margin: it clips the widest y-axis label. */}
      <AreaChart data={points} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="signups-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={chartColors.primary} stopOpacity={0.18} />
            <stop offset="100%" stopColor={chartColors.primary} stopOpacity={0.01} />
          </linearGradient>
        </defs>

        {/* Horizontal rules only — vertical ones add nothing over a time axis. */}
        <CartesianGrid stroke={chartColors.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="month" {...axisProps} />
        <YAxis {...axisProps} width={32} allowDecimals={false} />

        <Tooltip content={<ChartTooltip />} cursor={{ stroke: chartColors.axis, strokeWidth: 1 }} />

        <Area
          type="monotone"
          dataKey="signups"
          name="signups"
          stroke={chartColors.primary}
          strokeWidth={2}
          fill="url(#signups-fill)"
          // Dots only on hover: a marker on every point is noise at 12 points.
          dot={false}
          activeDot={{ r: 4, strokeWidth: 2, stroke: chartColors.surface }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
